/**
 * A fighter with nobody holding the controller.
 *
 * This replaces `dummy.js`, which was a coin toss on a timer: it did not read
 * the fight, and the only thing it could do about what it read was walk forward
 * or throw one jab. What is here reads the state both fighters are actually in
 * and decides what to *press*.
 *
 * Two rules shape the whole module, and they are the reason it is as small as
 * it is.
 *
 * **It presses buttons.** The AI owns a `virtualInput` and a `playerControl`,
 * and it drives the fighter through exactly the path a player does — the same
 * binding, the same combo resolution, the same strikes-before-movement order,
 * the same escape-from-a-hold branch. It never calls `throwStrike` itself.
 * That is not politeness: every gate in this game lives on that path, and the
 * last two build steps were both spent finding rules that a second caller had
 * quietly walked around. A slam this thing asks for against a fighter on their
 * feet is refused by `requiresStun` because it is refused for *everybody*, and
 * nothing here had to know that rule exists.
 *
 * **It reads state, not intent.** Everything it looks at is something that has
 * already resolved into the world: a machine state, an open vulnerability
 * window, a health bar, a weapon on the floor. It is handed no input layer but
 * its own and cannot see one. A punch is visible to it once the punch has
 * started, which is the same moment it becomes visible to a player — and what
 * the startup frames are *for*. See `difficulty` in step 49 for the delay that
 * is then applied on top of that.
 *
 * Pure, like everything in `core`: no three.js, no renderer, nothing that needs
 * a browser. The whole thing can be run headless at a thousand frames a second,
 * which is what step 50 does with it.
 */

import {
  GRAPPLE_BINDINGS,
  LANES,
  createPlayerControl,
  grappleFor,
  stickToward,
} from './playerControl.js';
import { createVirtualInput } from './virtualInput.js';
import { blowFrom } from './strikeLayer.js';
import { matchesKO } from './styles.js';
import { difficultyFor } from './difficulty.js';
import {
  chooseTarget,
  herdStick,
  readArena,
  targetById,
  validateRingcraft,
  withinReach,
} from './ringcraft.js';

const ZERO = Object.freeze({ x: 0, y: 0 });
const FORWARD = Object.freeze({ x: 0, y: 1 });
const BACK = Object.freeze({ x: 0, y: -1 });

/**
 * The stick position each lane is asked for with.
 *
 * The AI reaches a follow-up the same way a player does — by pushing the stick
 * — rather than by naming the move. Movement is refused for the whole of a
 * strike, so a vector pushed on the frame it commits steers nothing; it only
 * says which route it meant.
 */
const LANE_STICK = Object.freeze({ neutral: ZERO, forward: FORWARD, back: BACK });

/** The verbs it can attack with, heavy first, which is the order a player's
 * control layer reads them in. */
const VERBS = Object.freeze(['heavy', 'light']);

/**
 * The verbs that fire on the edge rather than on the hold.
 *
 * `block` and `run` are read with `held()` and mean "for as long as this is
 * down". Everything else is read with `pressed()` and means "now" — and a verb
 * that is still down from last frame cannot be pressed again without being let
 * go of first. A player's fingers do that by themselves; this has to.
 */
const TAPPED = Object.freeze(['light', 'heavy', 'grapple', 'taunt', 'blazin']);

/**
 * The parts of the tuning that are not difficulty.
 *
 * Difficulty is data and lives in its own file; these are the numbers that
 * describe how *any* fighter AI behaves, at any tier.
 */
export const AI = Object.freeze({
  /** Seconds between re-deciding what the plan is. Drawn from the range. */
  beat: Object.freeze([0.3, 0.7]),
  /** The fraction of a move's reach it wants to stand at. Inside 1: the reach
   * is where the fist *stops*, and standing exactly there is standing at the
   * edge of every miss. */
  standoff: 0.92,
  /** How often a held fighter tries the escape, in seconds. A mash, not a
   * read: the escape window is not something a player can see. */
  mash: 0.12,
  /**
   * Seconds between something happening and this fighter seeing it.
   *
   * Zero here is not the intended setting — every tier in `difficulty.json`
   * gives it a real value — it is the neutral default a tuning number gets when
   * nobody has said otherwise.
   */
  reaction: 0,
  /** How hard it presses, 0..1. Difficulty overrides this. */
  pressure: 0.55,
  /** Chance of guarding a committed attack. Difficulty overrides this. */
  block: 0.5,
  /** Chance of taking a free punish on an open window. Difficulty overrides. */
  punish: 0.5,
  /** Chance of giving ground rather than trading, when it is the hurt one. */
  cover: 0.6,
  /** Chance of taking the cancel window a landed blow just opened. */
  chain: 0.7,

  /** How the two danger flags bend the tempo. */
  pressScale: 1.7,
  pressBeat: 0.55,
  coverScale: 0.45,

  /** How many links ahead it will look for a chain that ends the fight. Three
   * is the longest chain the move data actually declares. */
  lookahead: 3,
  /** Beyond this many multiples of its range, it runs in rather than walks. */
  runAt: 2,
  /** Below this fraction of its range it is too close to use its best tool. */
  space: 0.55,
  /** How far it will cross the floor for a weapon, in metres. */
  fetch: 4,
  /** How much further than a move's reach still counts as inside it. Bodies
   * are not points and a strike travels while it is out. */
  margin: 0.25,

  /**
   * How far away a piece of the room is still worth walking somebody toward,
   * in metres. Wider than the whip's own distance on purpose: herding is the
   * part that happens *before* anything is in range, and a fighter who only
   * looked as far as they could throw would never set anything up. Capped so it
   * does not cross the whole platform for a wall behind it.
   */
  ringside: 6,
  /**
   * Seconds of headlights that count as "the train is coming".
   *
   * The whip takes four tenths of a second and the telegraph runs for three, so
   * a fighter who waited for the service to actually arrive would start the
   * grapple as it left. This is how early they are allowed to commit.
   */
  trainWindow: 2.5,
});

/** Distance on the floor. Height is not a thing two standing fighters differ in. */
export const gapBetween = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

/**
 * One fighter, as the AI is allowed to see them.
 *
 * Deliberately a flat snapshot of public getters rather than the fighter
 * itself: what is in this object is the definition of what the AI may know,
 * and a reference to the live combatant would make that definition unwritable.
 * It is also what makes the perception delay in step 49 possible at all — you
 * cannot put a live object in a queue and call it "half a second ago".
 */
export function readFighter(who, grapple = null) {
  const health = who?.health ?? null;
  const strikes = who?.strikes ?? null;
  return {
    id: who?.id ?? null,
    state: who?.machine?.state ?? null,

    // Both layers, and the flag that hangs off them.
    wellness: health?.wellness ?? 0,
    consciousness: health?.consciousness ?? 0,
    inDanger: Boolean(health?.inDanger),
    spent: Boolean(health?.isSpent),
    out: Boolean(health?.isOut),
    brokenLimb: health?.brokenLimb ?? null,

    momentum: who?.momentum?.fraction ?? 0,
    canIgnite: Boolean(who?.momentum?.canIgnite),
    blazin: Boolean(who?.blazin),
    heat: who?.heat?.fraction ?? 0,

    /** What is in their hands, if anything. Visible across the arena. */
    armed: Boolean(who?.armed),
    weapon: who?.weapon?.id ?? null,

    /** The two windows a fighter can be caught in. */
    stunned: Boolean(who?.stunned),
    vulnerable: Boolean(who?.vulnerable),

    /**
     * Mid-strike, and how far through. A commitment that has already started
     * — the telegraph, not the intent behind it.
     */
    striking: Boolean(strikes?.active),
    strike: strikes?.strike ?? null,
    phase: strikes?.phase ?? null,
    /** How far the thing in flight reaches. Read off the move that is already
     * swinging, which is the animation anybody watching can see. */
    strikeReach: (strikes?.active && strikes.get?.(strikes.strike)?.reach) || 0,
    guarding: who?.machine?.state === 'block',

    /** The cancel window a landed blow opens, and how long is left of it. */
    chaining: Boolean(who?.combo?.open),
    chain: who?.combo?.length ?? 0,

    held: Boolean(grapple?.isHeld?.(who)),
    holding: Boolean(grapple?.isHolding?.(who)),
  };
}

/** The nearest thing lying on the floor, or null when the floor is clear. */
export function nearestWeapon(who, ground) {
  let best = null;
  for (const item of ground?.items ?? []) {
    const distance = gapBetween(who.position, item);
    if (!best || distance < best.distance) best = { id: item.weapon?.id ?? null, distance, x: item.x, z: item.z };
  }
  return best;
}

/**
 * The whole fight, as one frozen look at it.
 *
 * `room` is the arena — the walls, the pillars, the pit and the train — read
 * from where the *other* fighter is standing, because every question it answers
 * is about where somebody else is going to end up. Without an arena it is the
 * empty room, and everything downstream of it does nothing, which is what every
 * test that predates step 61 is looking at.
 */
export function readFight({ combatant, opponent, grapple = null, ground = null, arena = null }) {
  return {
    gap: gapBetween(combatant.position, opponent.position),
    self: readFighter(combatant, grapple),
    foe: readFighter(opponent, grapple),
    floor: nearestWeapon(combatant, ground),
    room: readArena({ foe: opponent.position, arena }),
  };
}

export function createFighterAI({
  combatant,
  opponent,
  grapple = null,
  /** The weapon ground, so it can see what has been thrown in. */
  ground = null,
  /**
   * The room. Re-pointed by `createArenaHost` when one is swapped, in the same
   * loop that re-floors everybody's locomotion — because a fighter aiming at
   * the last arena's pillars is the same bug as one walking its rectangle.
   */
  arena = null,
  /**
   * What this fighter does with a room, out of their roster entry. Null is
   * everybody the file does not name, and does nothing at all.
   */
  ringcraft = null,
  random = Math.random,
  /** A tier name from `difficulty.json`, or a tier object. Null takes the default. */
  difficulty = null,
  /** Tuning numbers laid over the tier. For tests, mostly. */
  tuning = null,
  ...overrides
}) {
  // `let`, not `const`: a fighter's tier changes when the person in the slot
  // changes, and the alternative — building a new AI — is what makes every
  // reference to this one stale. See `retier`.
  let config = { ...AI, ...difficultyFor(difficulty), ...tuning };
  // Same reasoning as `config`: the person in the slot changes, and rebuilding
  // the AI to change them is what makes every reference to it stale (gotcha 49).
  let trait = validateRingcraft(ringcraft, { where: combatant?.id ?? 'a fighter' });
  let room = arena;
  const input = createVirtualInput();
  // The player's own control layer, with a controller nobody is holding.
  const control = createPlayerControl({ combatant, opponent, input, grapple, ...overrides });

  // Read out of `config` on every call rather than destructured once, so a
  // change of tier takes effect on the next beat instead of never.
  const nextBeat = () => {
    const [shortest, longest] = config.beat;
    return shortest + random() * (longest - shortest);
  };

  let plan = 'hold';
  /** The last thing it actually did, which is not always the standing plan: a
   * strike is one frame long and the plan behind it is already back to `hold`
   * by the time anybody could read it. */
  let acted = 'hold';
  let clock = nextBeat();
  let mashClock = 0;
  let view = readFight({ combatant, opponent, grapple, ground, arena: room });

  /**
   * What it has seen of the other fighter, and when.
   *
   * The reaction delay is a *real* delay rather than a dice roll: the other
   * fighter is read out of this queue, `reaction` seconds late, so a rookie is
   * not a champion with a handicap — it is genuinely looking at an older fight,
   * and answering a punch that has already landed.
   *
   * Its own state is read live, and so is the gap. Knowing what your own hands
   * are doing costs nothing, and watching a body move across a floor is
   * continuous — it is *events* that cost time to notice. A delay on the
   * distance would make a champion walk into walls.
   */
  const memory = [];
  let elapsed = 0;

  /** What `verb` in `lane` would throw right now, as the control layer resolves it. */
  const moveFor = (verb, lane = 'neutral') =>
    combatant.strikes.get(control.resolve(verb, lane)) ?? null;

  /**
   * How much further (or nearer) every blow lands on *this* opponent than on
   * the mannequin the numbers were measured on. Its own reach is already in
   * the move — the strike layer serves this body's — and the other half of a
   * reach is the body it is aimed at: on fighter-b every move lands 11.5 cm
   * shorter. Read live: a body is as visible as the gap is.
   */
  const against = () => opponent?.reachAgainst ?? 0;

  /**
   * How close it wants to be: the reach of the move it actually means to
   * throw, pulled in by the standoff.
   *
   * The reach of its *longest* move is the wrong answer and was the first one
   * here. A kickboxer's jab reaches 1.65 and their cross 1.53, and a fighter
   * who stands at jab range and then throws the cross — because the cross is
   * worth three times as much — is a fighter who has carefully positioned
   * themselves 4cm outside their own punch. Measured live, that was a 50% whiff
   * rate against a target that was not even moving.
   *
   * So the range follows the *choice*, and both are recomputed every frame:
   * halfway through a combo the button means a different move with a different
   * reach, and a bat is not a fist.
   */
  function preferredRange({ seeking = false } = {}) {
    const move = pick({ seeking });
    return move ? (move.reach + against()) * config.standoff : 0;
  }

  /**
   * The move it would throw, of the two the buttons offer.
   *
   * `within` filters to what can actually arrive from here — and it is the
   * move's own reach, with no margin added. The margin exists for judging
   * whether somebody *else's* strike can arrive, where being generous is
   * cautious; added to its own attacks it is simply throwing punches 25cm
   * short of the body and calling it a plan.
   *
   * `seeking` prefers the move that would end it by this style's own rule over
   * the one that merely hurts most, which is the whole of "go for your KO
   * condition" — and the reason it is a preference rather than a separate plan
   * is that the button is the same button.
   */
  function pick({ within = null, seeking = false } = {}) {
    let best = null;
    for (const verb of VERBS) {
      for (const lane of LANES) {
        const move = moveFor(verb, lane);
        if (!move) continue;
        if (within !== null && move.reach + against() < within) continue;
        const candidate = {
          verb,
          lane,
          ...move,
          finisher: seeking && finishes(move),
          opener: seeking && opensOnto(control.resolve(verb, lane)),
        };
        // Ends it, then leads to something that ends it, then hurts most.
        if (!best || rank(candidate) > rank(best)) best = candidate;
      }
    }
    return best;
  }

  const verbInReach = (gap, options = {}) => pick({ ...options, within: gap });

  /** How good a candidate is, in the order the three things matter. */
  const rank = (move) => (move.finisher ? 2e6 : 0) + (move.opener ? 1e6 : 0) + move.damage;

  /**
   * Whether any hold this fighter knows would satisfy their own KO condition.
   *
   * Asked of the *moves* rather than of the condition's name, because two
   * styles win with a hold in quite different ways: wrestling's `kind` test
   * takes any grapple, and submissions' `limb` test takes an armbar and nothing
   * else. Reading the test's name would have found the first and missed the
   * second, which is how the `limb` condition came to be unreachable.
   */
  function koIsHold() {
    if (!combatant.loadout?.ko) return false;
    return (combatant.moves ?? []).some((name) => {
      const move = combatant.strikes.get(name);
      return move?.kind === 'grapple' && finishes(move);
    });
  }

  /**
   * Which hold the grapple button would mean, and what has to be held down to
   * mean it.
   *
   * The same three slots the player has, asked the same way — `grappleFor` is
   * the binding, and the AI has no private door to a move that is not on it.
   * When it is hunting a finish it walks the slots for one that would end the
   * fight; otherwise it takes whatever the plain press offers.
   */
  function holdFor({ seeking = false } = {}) {
    const moves = combatant.moves ?? [];
    const get = (name) => combatant.strikes.get(name);
    const stunned = Boolean(view.foe.stunned);
    const slots = [null, 'light', 'heavy'].map((modifier) => ({
      modifier,
      name: grappleFor({ moves, get, stunned, modifier }),
    }));
    const plain = slots[0];
    if (!seeking) return plain;

    const ends = slots.find((slot) => slot.name && finishes(get(slot.name)));
    if (ends) return ends;

    // A limb style is the one case where the finishing move is not available
    // *yet*. `wouldBreakLimb` is false until the arm is nearly gone, and the
    // only thing that gets it there is the same armbar — so a fighter who only
    // reached for what would finish would never throw the ones that make
    // finishing possible. Working the limb is the plan; ending it is the
    // consequence.
    if (combatant.loadout?.ko?.test === 'limb') {
      const works = slots.find((slot) => slot.name && get(slot.name)?.limbTarget);
      if (works) return works;
    }
    return plain;
  }

  /** A hold this fighter knows that works a limb, when that is how they win. */
  function limbHold() {
    if (combatant.loadout?.ko?.test !== 'limb') return null;
    return (
      (combatant.moves ?? []).find((name) => {
        const move = combatant.strikes.get(name);
        return move?.kind === 'grapple' && move.limbTarget;
      }) ?? null
    );
  }

  /** How far the clinch reaches, which is the range a grapple plan wants. */
  const grappleReach = () => (combatant.strikes.get(GRAPPLE_BINDINGS.opener)?.reach ?? 0) + against();

  /**
   * How far a whip actually sends somebody.
   *
   * Read off the hold being *held* when there is one, and off the opener when
   * there is not — the number is a property of the move rather than of the
   * fighter, and the plan that decides where to stand is made long before
   * anybody is in anybody's hands.
   */
  function whipDistance() {
    const held = grapple?.isHolding?.(combatant) ? grapple.move : null;
    const move = held ?? combatant.strikes.get(GRAPPLE_BINDINGS.opener);
    return move?.whip?.distance ?? 0;
  }

  /**
   * Whether this fighter uses the room at all. The file decides, not the tier —
   * difficulty may not buy an advantage, and knowing where the pillars are is
   * one.
   *
   * `herd` is not in here on purpose: walking somebody toward a wall you would
   * never throw them at is walking for its own sake, and `chooseTarget` returns
   * nothing without one of these two anyway.
   */
  const usesTheRoom = () => trait.aim > 0 || trait.pit > 0;

  /**
   * Where it would send somebody, chosen once and then kept.
   *
   * The same reasoning as `once`: a roll taken on every frame of a hold is not a
   * chance, it is a certainty arriving within a few frames, and the trait
   * numbers would mean nothing. What is kept is the target's **id** rather than
   * the target — the fighters are still moving, and the direction to a pillar
   * from where they are now is not the direction from where they were when the
   * grapple started.
   */
  const aiming = { key: null, id: null };
  /** What the standing `herd` plan is walking toward, so the step survives the beat. */
  let herding = null;

  function aimTarget() {
    if (!usesTheRoom()) return null;
    if (aiming.key !== 'hold') {
      aiming.key = 'hold';
      aiming.id =
        chooseTarget(view.room, trait, {
          within: whipDistance(),
          trainWindow: config.trainWindow,
          random,
        })?.id ?? null;
    }
    return targetById(view.room, aiming.id);
  }

  /** The piece of the room this fighter is working toward, on this beat. */
  function roomPlan() {
    if (!usesTheRoom() || !view.room.any) return null;
    const target = chooseTarget(view.room, trait, {
      within: config.ringside,
      trainWindow: config.trainWindow,
      random,
    });
    if (!target) return null;
    return { target, ready: withinReach(target, whipDistance()) };
  }

  /**
   * Whether `move`, thrown right now, would satisfy this fighter's style.
   *
   * Asked of the blow the move would *become*, not of the move, because that
   * is what `matchesKO` is written against — and it is the only way a chain
   * condition like kickboxing's can be answered at all: the same roundhouse is
   * a KO at the end of a combo and an ordinary kick on its own.
   */
  function finishes(move, chainIndex = combatant.combo?.length ?? 0) {
    const ko = combatant.loadout?.ko;
    if (!ko || !move) return false;
    return matchesKO(
      ko,
      blowFrom(move, { chainIndex, part: move.limbTarget ?? 'chest', damage: move.damage }),
      opponent
    );
  }

  /**
   * Whether throwing `name` opens a road to a move that would end it.
   *
   * Kickboxing wins with *a kick at the end of a combo*, and a fighter who only
   * ever throws the hardest single move it has can never satisfy that: the
   * cross is worth three times a jab and leads nowhere this fighter knows, so
   * without this the style's own knockout condition is unreachable by the thing
   * that is supposed to be hunting it.
   *
   * Walks the move data's declared follow-ups, filtered to what this fighter
   * was actually taught — `combatant.follows` already does that filtering — and
   * counts the links, because a chain condition is a question about how deep
   * into a combo the blow lands.
   */
  function opensOnto(name, depth = config.lookahead, chainIndex = combatant.combo?.length ?? 0) {
    if (depth <= 0) return false;
    for (const next of combatant.follows?.(name) ?? []) {
      const move = combatant.strikes.get(next);
      if (!move) continue;
      if (finishes(move, chainIndex + 1)) return true;
      if (opensOnto(next, depth - 1, chainIndex + 1)) return true;
    }
    return false;
  }

  /**
   * The move vector that walks toward a point on the floor.
   *
   * The stick is read relative to the *opponent* — +y closes the gap, x swings
   * around them — so a destination has to be resolved into that basis. It is an
   * approximation, because locomotion solves the step as an arc around the
   * other fighter rather than a straight line, and it is the right one: a
   * fighter crossing the floor for a bat does not stop circling their opponent
   * to do it.
   */
  function towards(point) {
    const self = combatant.position;
    const other = opponent.position;
    const fx = other.x - self.x;
    const fz = other.z - self.z;
    const span = Math.hypot(fx, fz) || 1;
    const forward = { x: fx / span, z: fz / span };
    const right = { x: -forward.z, z: forward.x };
    const dx = point.x - self.x;
    const dz = point.z - self.z;
    const length = Math.hypot(dx, dz);
    if (length < 1e-4) return ZERO;
    const direction = { x: dx / length, z: dz / length };
    return {
      x: direction.x * right.x + direction.z * right.z,
      y: direction.x * forward.x + direction.z * forward.z,
    };
  }

  /**
   * Whether the thing in flight can actually arrive.
   *
   * A punch thrown from across the platform is not a threat, and a fighter who
   * blocks one is a fighter standing still for no reason. The margin is the
   * same one an attack is chosen with: a strike travels while it is out.
   */
  const incoming = (foe, gap) =>
    foe.striking && gap <= foe.strikeReach + (combatant.reachAgainst ?? 0) + config.margin;

  /**
   * Each window gets one decision, taken on its opening frame and kept.
   *
   * Re-rolling every frame would turn a 50% block into a certainty — sixty
   * chances a second at not blocking is no chance at all — and would make the
   * difficulty numbers mean nothing. `key` is what identifies the window: the
   * name of the strike in flight, or simply that a window is open.
   *
   * A decision that has been *acted* on is spent, which is a separate thing
   * from having been taken. Guarding is not spent — the answer is "block this
   * one" and the guard stays up for as long as the strike is out — but a
   * punish is: one swing per opening, not one per frame.
   */
  function once(store, key, chance) {
    if (key === null) {
      store.key = null;
      return false;
    }
    if (store.key !== key) {
      store.key = key;
      store.answer = random() < chance;
      store.spent = false;
    }
    return store.answer && !store.spent;
  }

  /** Marks a decision as acted on. Called only once the press actually went out. */
  const spend = (store) => {
    store.spent = true;
  };

  const guard = { key: null, answer: false };
  const punish = { key: null, answer: false };
  const grab = { key: null, answer: false };
  const link = { key: null, answer: false };

  /**
   * What to press this frame.
   *
   * A ladder, in priority order, and every rung returns an action set rather
   * than doing anything: nothing below here touches the fighter. The order is
   * the behaviour. What is happening *to* it outranks what it had planned, a
   * free opening outranks its own tempo, and its tempo outranks standing still.
   */
  function decide(dt) {
    const { self, foe, gap, floor } = view;

    // Being held. There is nothing to decide and one thing to do, and the
    // control layer turns the grapple button into an escape attempt by itself.
    if (self.held) {
      mashClock -= dt;
      const tap = mashClock <= 0;
      if (tap) mashClock = config.mash;
      plan = 'escape';
      return { plan, move: ZERO, actions: tap ? ['grapple'] : [] };
    }

    // Holding one. The stick is aim now — push it and they go that way — but
    // **a hold that pays out is a hold worth keeping**. An armbar's damage
    // lands when the hold runs its course, so whipping is throwing away the
    // entire move; the first submissions AI to hold anybody whipped them, and
    // the arm it was working never took a point.
    if (self.holding) {
      const held = grapple?.move ?? null;
      // Working a limb is never traded away. `wouldBreakLimb` is false until the
      // arm is nearly gone and the only thing that gets it there is this hold,
      // so a fighter who let go to aim at a wall would be a fighter whose style
      // can never win (gotcha 75).
      const limbWork = combatant.loadout?.ko?.test === 'limb' && Boolean(held?.limbTarget);
      // Everything else, a fighter who uses the room would rather put you
      // through it than squeeze. A finishing hold pays out in a second and a
      // half; a pillar pays out now, and a pillar with a train behind it does
      // not ask how healthy anybody is.
      const target = limbWork ? null : aimTarget();
      const worth = !target && (limbWork || (held && finishes(held)));
      if (worth) {
        plan = 'work';
        return { plan, move: ZERO, actions: [] };
      }
      if (target) {
        // Pushed as a *stick*, not written into `grapple.whip`. The whole
        // module is built on going down the player's path, and a second caller
        // into the grapple is the thing the header is about.
        plan = 'aim';
        return { plan, move: stickToward(combatant.facing, target.toward), actions: [] };
      }
      plan = 'whip';
      return { plan, move: FORWARD, actions: [] };
    }
    // Not holding anybody: whatever it had decided to aim at is spent.
    aiming.key = null;

    // Nothing left to drive: one of them is finished.
    //
    // `spent` is deliberately not in here. A body that has given out from under
    // a fighter who is still awake is a fighter who is about to lose, not one
    // who has — and standing still for the second and a half it takes the
    // ceiling to claim their last point of consciousness is giving up early.
    if (self.out || foe.out) {
      plan = 'done';
      return { plan, move: ZERO, actions: [] };
    }

    const actions = [];
    // A full meter spends itself. It costs no time and is refused below full,
    // so there is never a frame where holding it back is the better play.
    if (self.canIgnite) actions.push('blazin');

    // Where it wants to stand follows what it means to throw, and what it
    // means to throw changes when the other one is nearly gone.
    const range = preferredRange({ seeking: foe.inDanger });

    // --- 1. something is already coming ------------------------------------
    //
    // The one rung that is a reaction rather than a plan, and the reason it is
    // first: a fighter who finishes their own thought while a roundhouse is on
    // its way is a fighter who eats it.
    if (incoming(foe, gap)) {
      if (once(guard, foe.strike, config.block)) {
        plan = 'block';
        return { plan, move: ZERO, actions: [...actions, 'block'] };
      }
      // Not blocking this one. Backing out of it is the other answer, and the
      // reach it is backing out of is the reach of the move being thrown.
      plan = 'evade';
      return { plan, move: BACK, actions };
    }
    once(guard, null);

    // --- 2. they have left themselves open ---------------------------------
    //
    // The whiff window is short and already exists: `vulnerable` is opened by
    // the strike layer when a move misses and spent by the first blow that
    // cashes it in. Nothing new is measured here — it is read.
    if (foe.vulnerable && once(punish, 'open', config.punish)) {
      const swing = verbInReach(gap, { seeking: foe.inDanger });
      plan = 'punish';
      if (swing) {
        return {
          plan,
          move: LANE_STICK[swing.lane] ?? ZERO,
          actions: [...actions, swing.verb],
          tap: swing.verb,
          commit: () => spend(punish),
        };
      }
      // Too far to punish. Close it — a window this short is worth a sprint.
      return { plan, move: FORWARD, actions: [...actions, 'run'] };
    }
    if (!foe.vulnerable) once(punish, null);

    // --- 3. reeling off something solid ------------------------------------
    //
    // The finisher is only legal inside this window and the binding reaches for
    // it by itself. Whether it is *allowed* is `requiresStun`'s business, not
    // this module's — asking is free and being refused costs one frame.
    if (foe.stunned && gap <= grappleReach() + config.margin && once(grab, 'stun', 1)) {
      plan = 'finish';
      return {
        plan,
        move: ZERO,
        actions: [...actions, 'grapple'],
        tap: 'grapple',
        commit: () => spend(grab),
      };
    }
    if (!foe.stunned) once(grab, null);

    // --- 4. the window a landed blow just opened ---------------------------
    //
    // A cancel window is a fifth of a second and the beat is half of one, so a
    // fighter who waits for its own tempo to continue a combo never continues
    // one — and a style that wins with *a kick at the end of a combo* never
    // wins. The window is the opportunity; the beat is for when there is none.
    if (self.chaining && once(link, self.chain, config.chain)) {
      const swing = verbInReach(gap, { seeking: foe.inDanger });
      if (swing) {
        plan = 'chain';
        return {
          plan,
          // The stick is what picks the route. A chain is exactly where the
          // lanes matter, and exactly where the AI would otherwise be stuck
          // with whichever follow-up came first in the file.
          move: LANE_STICK[swing.lane] ?? ZERO,
          actions: [...actions, swing.verb],
          tap: swing.verb,
          commit: () => spend(link),
        };
      }
    }
    if (!self.chaining) once(link, null);

    // --- 5. the tempo ------------------------------------------------------
    clock -= dt;
    if (clock <= 0) {
      clock += nextBeat() * (foe.inDanger ? config.pressBeat : 1);
      plan = choosePlan({ self, foe, gap, floor, range });
    }

    switch (plan) {
      case 'fetch': {
        // The weapon may have been taken or timed out between the beat and now.
        if (self.armed || !floor) {
          plan = 'hold';
          break;
        }
        return { plan, move: towards(floor), actions };
      }
      case 'herd': {
        // Re-read every frame rather than held from the beat: both fighters are
        // moving and the bearing is measured off where the other one is *now*.
        const target = targetById(view.room, herding);
        if (!target) {
          plan = 'hold';
          break;
        }
        return {
          plan,
          move: herdStick(combatant.position, opponent.position, target, {
            range: grappleReach(),
          }),
          // Worth running for. The whole plan is being somewhere before they
          // are, and a walk that arrives after they have moved is not a plan.
          actions: gap > range * config.runAt ? [...actions, 'run'] : actions,
        };
      }
      case 'close':
        return {
          plan,
          move: FORWARD,
          actions: gap > range * config.runAt ? [...actions, 'run'] : actions,
        };
      case 'space':
        return { plan, move: BACK, actions };
      case 'retreat':
        // Giving ground, and *only* that. Blocking plants a fighter — the
        // guard is a locomotion state and it competes with walking — so a
        // retreat that also held block would be a fighter standing still with
        // their hands up, which is not a retreat. The guard goes up on the
        // rung above this one, when something is actually on its way.
        return { plan, move: BACK, actions };
      case 'grab': {
        // The hold it means, and the verb it has to hold down to mean it.
        const { modifier, name } = holdFor({ seeking: true });
        if (!name) {
          plan = 'hold';
          break;
        }
        return {
          plan,
          move: ZERO,
          actions: [...actions, 'grapple', ...(modifier ? [modifier] : [])],
          tap: 'grapple',
          // The modifier is *held*, not pressed, so it must survive the rule
          // that stops a tapped verb being leaned on — it is doing the opposite
          // job here.
          keep: modifier ? [modifier] : [],
          commit: () => {
            plan = 'hold';
          },
        };
      }
      case 'strike': {
        const swing = verbInReach(gap, { seeking: foe.inDanger });
        // Nothing reaches: only possible if the move set changed under the
        // beat, since the range that chose `strike` is measured off the same
        // moves.
        if (!swing) {
          plan = 'close';
          return { plan, move: FORWARD, actions };
        }
        // One swing per beat, thrown or refused — but only once the press has
        // actually gone out. A verb that had to be let go of first would
        // otherwise cost the whole beat.
        return {
          plan,
          move: LANE_STICK[swing.lane] ?? ZERO,
          actions: [...actions, swing.verb],
          tap: swing.verb,
          commit: () => {
            plan = 'hold';
          },
        };
      }
      default:
        break;
    }

    return { plan: 'hold', move: ZERO, actions };
  }

  /**
   * What to do with the next beat, when nothing is being done *to* it.
   *
   * Spacing is the default and the two danger flags bend everything around it:
   * a hurt fighter gives ground and swings less, and a fighter whose opponent
   * is hurt shortens the beat and presses.
   */
  function choosePlan({ self, foe, gap, floor, range }) {
    // Something on the floor, nothing in its hands, and closer than the fight
    // is. A weapon is worth more than a beat of pressure, and the pickup is a
    // matter of standing on it — `weapons.js` hands it over.
    if (!self.armed && floor && floor.distance <= config.fetch) return 'fetch';

    // Its own style's answer, when the other one is nearly gone: a hold is the
    // KO condition and there is a body in front of it to put hands on.
    if (foe.inDanger && koIsHold() && gap <= grappleReach() + config.margin) return 'grab';
    // A style that wins by working a limb does not wait for danger: the limb
    // has to be taken apart first, and there is no other way to take it.
    if (limbHold() && gap <= grappleReach() + config.margin) {
      return random() < config.pressure ? 'grab' : 'hold';
    }

    // Hurt, and they are not. Give ground rather than trade — the consciousness
    // bar comes back on its own, and only if nothing is landing on it.
    if (self.inDanger && !foe.inDanger && random() < config.cover) return 'retreat';

    if (gap > range * 1.05) return 'close';
    // Too close to use the longest thing it has. Reset the spacing rather than
    // fight at a range its own move set is wrong for.
    if (gap < range * config.space) return 'space';

    const pressure =
      config.pressure * (foe.inDanger ? config.pressScale : self.inDanger ? config.coverScale : 1);

    // --- the room ---------------------------------------------------------
    //
    // **Down here, and split across the pressure roll, on purpose.** The first
    // version of this sat above the spacing and took the whole beat, and the
    // fighter it produced spent every round walking people toward pillars and
    // never hitting them: sixty rounds, sixty losses, and a body is only
    // finished on a wall if it was already going down (`isFinisher`). A
    // specialist who never wins an exchange never gets to use the room.
    //
    // So it does not replace fighting. It replaces *what* an attack is, and
    // what a quiet beat is spent on.
    const room = roomPlan();

    if (random() < pressure) {
      // Attacking, and the wall is already within a whip of them: the attack is
      // a grapple. Same beat, same aggression, different tool.
      if (room?.ready && gap <= grappleReach() + config.margin) return 'grab';
      return 'strike';
    }

    // Not attacking. A fighter who uses the room spends the beat they would
    // have stood still on getting to a better part of it — between them and
    // the pillar, because nothing here lets one body push another and an
    // opponent closes on whoever they are fighting.
    if (room && !room.ready && random() < trait.herd) {
      herding = room.target.id;
      return 'herd';
    }
    herding = null;
    return 'hold';
  }

  return {
    /** The arena host re-points this when the floor changes. */
    locomotion: control.locomotion,

    /**
     * And this, in the same loop and for the same reason.
     *
     * An AI aiming at the last arena's pillars is the failure `createArenaHost`
     * exists to prevent — it is the same bug as one walking the old rectangle,
     * and the fix belongs in the same place rather than at the call site.
     *
     * Only the three things it may know are kept, rather than the arena: a rule
     * module holding a thing with an `object3D` on it is a seam waiting to be
     * crossed.
     */
    setArena(next) {
      room = next ? { surfaces: next.surfaces, pit: next.pit, train: next.train } : null;
      aiming.key = null;
      herding = null;
      return room;
    },

    /**
     * What this fighter does with a room. In place, like `retier`, and for the
     * identical reason — the slot outlives the person standing in it.
     */
    ringcraft(next) {
      trait = validateRingcraft(next, { where: combatant?.id ?? 'a fighter' });
      aiming.key = null;
      herding = null;
      return trait;
    },
    /** The trait as it stands, for the overlay and the tests. */
    get room() {
      return trait;
    },
    /** Exposed for tests and the overlay; nothing in the game writes to them. */
    control,
    input,
    /**
     * A getter, not a value. `config` is replaced wholesale by `retier`, so a
     * plain property would hand out the *old* tier's numbers forever after the
     * first change — and anything that wrote to it would be tuning an object
     * nothing reads.
     */
    get config() {
      return config;
    },

    /** The last look it took at the fight. */
    get view() {
      return view;
    },
    /** What it is doing, for the overlay. */
    get choice() {
      return acted;
    },
    /** The standing plan, which outlives a one-frame action. */
    get plan() {
      return plan;
    },
    get remaining() {
      return clock;
    },
    get preferredRange() {
      return preferredRange();
    },

    /** Which tier it is fighting at, and how that reads. */
    get tier() {
      return config.tier ?? null;
    },

    /**
     * Fights at a different tier from the next frame on.
     *
     * **In place, rather than by building a new AI.** The overlay holds this
     * object, the arena host holds its locomotion, and `main.js` calls its
     * `update` every frame — so replacing it means finding and re-pointing
     * three references, and the failure mode of that is an overlay reporting
     * on a fighter nobody is driving. Changing the numbers under one stable
     * object has no such failure mode.
     *
     * What it does *not* keep is what the old eyes saw: the perception queue
     * is emptied, because snapshots taken at a 0.36s delay are not the ones a
     * fighter reacting in 0.11s would be looking at.
     */
    retier(next) {
      config = { ...AI, ...difficultyFor(next), ...tuning };
      memory.length = 0;
      elapsed = 0;
      // Whatever it was part-way through deciding was decided by somebody else.
      plan = 'hold';
      clock = nextBeat();
      for (const store of [guard, punish, grab, link]) store.key = null;
      // The aim was taken by the old eyes as well.
      aiming.key = null;
      herding = null;
      return config.tier ?? null;
    },
    get describe() {
      return config.describe ?? null;
    },

    /** One frame: look, decide, press, and let the control layer do the rest. */
    update(dt) {
      elapsed += dt;
      const now = readFight({ combatant, opponent, grapple, ground, arena: room });
      memory.push({ at: elapsed, foe: now.foe });
      // Keep the newest snapshot that is already old enough to have been seen.
      while (memory.length > 1 && memory[1].at <= elapsed - config.reaction) memory.shift();
      // The other fighter is what is delayed; the gap, its own state and the
      // room are live. See `readArena` for why a wall is not an event.
      view = { ...now, foe: memory[0].foe };
      const intent = decide(dt);
      acted = intent.plan;
      // A verb still held from last frame is let go of for one frame before it
      // is pressed again. Without it, a fighter that decides to attack on two
      // frames running throws exactly one punch — the second frame is a hold,
      // and a hold is not a press. That cost the chain rung every follow-up it
      // ever tried to take: the jab's own button was still down.
      const actions = intent.actions.filter(
        (name) => intent.keep?.includes(name) || !(TAPPED.includes(name) && input.held(name))
      );
      // The decision is only spent once the press it was for has gone out.
      if (intent.tap && actions.includes(intent.tap)) intent.commit?.();
      input.push({ move: intent.move, actions });
      return { ...control.update(dt), plan: intent.plan, choice: intent.plan };
    },
  };
}
